// TabelleScenari.tsx
import React, { useState, useMemo } from "react";
import { ScenarioScores, ScenarioRow } from "../../../../../api/apiTypes";

interface TabelleScenariProps {
  scenarioScores?: ScenarioScores;
}

const TabelleScenari: React.FC<TabelleScenariProps> = ({ scenarioScores }) => {
  const [sortColumn1, setSortColumn1] = useState<number>(0);
  const [sortDirection1, setSortDirection1] = useState<"asc" | "desc">("desc");

  const [sortColumn2, setSortColumn2] = useState<number>(0);
  const [sortDirection2, setSortDirection2] = useState<"desc" | "desc">("desc");

  const scenariKeys = useMemo(
    () => (scenarioScores ? Object.keys(Object.values(scenarioScores)[0]) : []),
    [scenarioScores]
  );

  const mockScenari: ScenarioRow[] = useMemo(() => {
    if (!scenarioScores) return [];
    return Object.entries(scenarioScores).map(([intermediario, scenariObj]) => ({
      intermediario,
      scenari: scenariKeys.map((key) => scenariObj[key as keyof typeof scenariObj]?.value ?? 0),
    }));
  }, [scenarioScores, scenariKeys]);

  const mockPosizioni: ScenarioRow[] = useMemo(() => {
    if (!scenarioScores) return [];
    return Object.entries(scenarioScores).map(([intermediario, scenariObj]) => ({
      intermediario,
      scenari: scenariKeys.map((key) => scenariObj[key as keyof typeof scenariObj]?.position ?? 0),
    }));
  }, [scenarioScores, scenariKeys]);

  const sortData = (data: ScenarioRow[], column: number, direction: "asc" | "desc") =>
    [...data].sort((a, b) => {
      const valA = a.scenari[column];
      const valB = b.scenari[column];
      return direction === "asc" ? valA - valB : valB - valA;
    });

  const sortedData1 = useMemo(
    () => sortData(mockScenari, sortColumn1, sortDirection1),
    [mockScenari, sortColumn1, sortDirection1]
  );

  const sortedData2 = useMemo(
    () => sortData(mockPosizioni, sortColumn2, sortDirection2),
    [mockPosizioni, sortColumn2, sortDirection2]
  );

  const handleSort1 = (index: number) => {
    if (sortColumn1 === index) setSortDirection1(sortDirection1 === "asc" ? "desc" : "asc");
    else {
      setSortColumn1(index);
      setSortDirection1("desc");
    }
  };

  const handleSort2 = (index: number) => {
    if (sortColumn2 === index) setSortDirection2(sortDirection2 === "desc" ? "desc" : "desc");
    else {
      setSortColumn2(index);
      setSortDirection2("desc");
    }
  };

  const getCellColor = (rowIndex: number) => {
    if (rowIndex <= 2) return "bg-green-100";
    if (rowIndex <= 5) return "bg-yellow-100";
    return "";
  };

  if (!scenarioScores) {
    return <p className="pl-2 mt-4">Nessun dato disponibile</p>;
  }

  return (
    <div className="mt-8">
      {/* Prima tabella: Valori */}
      <h2 className="text-xl font-bold mb-6 pl-2">TABELLA CLASSIFICA PER SCENARI</h2>
      <div className="overflow-x-auto bg-surface rounded-lg shadow-md">
        <table className="min-w-full border-collapse">
        <thead className="bg-gray-50 text-xs text-textSecondary font-semibold border-b border-divider">
        <tr className="bg-white border-b border-divider text-textSecondary text-xs">
              <th className="py-4 px-2 font-semibold text-xs">Intermediario</th>
              {scenariKeys.map((key, i) => (
                <th key={i} className="py-4 px-2 font-semibold">
                  <button
                    onClick={() => handleSort1(i)}
                    className={`flex flex-col items-center w-full h-full text-xs text-center px-1 py-0.5 rounded ${
                      sortColumn1 === i ? "bg-gray-100 text-textPrimary" : "hover:bg-gray-50"
                    }`}
                  >
                    <span className="text-xxs">{key}</span>
                    {sortColumn1 === i && <span className="ml-1">{sortDirection1 === "asc" ? "▲" : "▼"}</span>}
                  </button>
                </th>
              ))}
              <th className="py-4 px-2 font-semibold text-xs text-center">Totale</th>
            </tr>
          </thead>
          <tbody>
            {sortedData1.map((row, rowIndex) => {
              const total = row.scenari.reduce((sum, v) => sum + v, 0);
              return (
                <tr key={row.intermediario} className="border-b border-divider text-textPrimary text-xs even:bg-gray-50">
                  <td className="py-4 px-2 font-medium">{row.intermediario}</td>
                  {row.scenari.map((val, i) => (
                    <td key={i} className={`py-4 px-2 text-center ${sortColumn1 === i ? "bg-gray-100" : ""}`}>
                      {val.toFixed(2)}
                    </td>
                  ))}
                  <td className="py-4 px-2 text-center font-semibold">{total.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>


      {/* Seconda tabella: Posizioni */}
      <h2 className="text-xl font-bold mb-6 pl-2 mt-8">TABELLA PUNTEGGIO SCENARI PER POSIZIONE</h2>
      <div className="bg-surface rounded-lg shadow-md overflow-x-auto">
        <table className="w-full text-left table-auto">
          <thead>
            <tr className="bg-white border-b border-divider text-textSecondary text-xs">
              <th className="py-4 px-2 font-semibold text-xs">Intermediario</th>
              {scenariKeys.map((key, i) => (
                <th key={i} className="py-4 px-2 font-semibold text-xs">
                  <button
                    onClick={() => handleSort2(i)}
                    className={`flex flex-col items-center w-full h-full text-xs text-center px-1 py-0.5 rounded ${
                      sortColumn2 === i ? "bg-gray-100 text-textPrimary" : "hover:bg-gray-50"
                    }`}
                  >
                    <span className="text-xs">{key}</span>
                    {sortColumn2 === i && <span className="ml-1">{sortDirection2 === "desc" ? "▲" : "▼"}</span>}
                  </button>
                </th>
              ))}
              <th className="py-4 px-2 font-semibold text-xs text-center">Totale</th>
            </tr>
          </thead>
          <tbody>
            {sortedData2.map((row, rowIndex) => {
              const total = row.scenari.reduce((sum, v) => sum + v, 0);
              return (
                <tr key={row.intermediario} className="border-b border-divider text-textPrimary text-xs even:bg-gray-50">
                  <td className="py-4 px-2 font-medium">{row.intermediario}</td>
                  {row.scenari.map((val, j) => (
                    <td key={j} className={`py-4 px-2 text-center ${j === sortColumn2 ? getCellColor(rowIndex) : ""}`}>
                      {val}
                    </td>
                  ))}
                  <td className="py-4 px-2 text-center font-semibold">{total}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TabelleScenari;